<template>
    <div v-if="showCardEvent">
        <v-card :flat="flat" class="kim-card pb-2">
            <!-- ELECTRONIC MEDICAL RECORD TITLE -->
            <template v-if="!hideTitles">
                <v-card-text>
                    <v-layout align-center>
                        <v-flex xs9 class="summary-title text-uppercase">
                            {{ title }}
                        </v-flex>
                        <v-flex xs3>
                            <v-checkbox
                                label="Todo"
                                v-model="allSelected"
                                hide-details
                                class="checkbox-all"
                                @click.stop="onAllSelected"
                            />
                        </v-flex>
                    </v-layout>
                </v-card-text>
                <v-divider/>
            </template>
            <!-- ELECTRONIC MEDICAL RECORD -->
            <div id="electronicMedicalRecord">

                <!-- PRINT BUTTON -->
                <div class="print-btn text-xs-right mt-2">
                    <v-chip @click="sendPdfSummary('electronicMedicalRecord')" label small outline color="secondary">
                        <v-progress-circular v-if="electronicMedicalRecordSending" indeterminate size="20" width="2" style="padding: 0 28px;"/>
                        <div v-else>
                            Enviar <v-icon size="14" class="ml-1">fas fa-paper-plane</v-icon>
                        </div>
                    </v-chip>
                    <v-chip @click="printSummary('electronicMedicalRecord')" label small outline color="secondary">
                        Imprimir <v-icon size="14" class="ml-1">fas fa-print</v-icon>
                    </v-chip>
                </div>

                <!-- PRINCIPAL INFO -->
                <div class="printable-section print d-none">
                    <div class="title text-uppercase">
                        FICHA CLÍNICA ELECTRÓNICA
                    </div>
                    <div class="subtitle" v-if="client">
                        Datos generales del paciente:
                    </div>
                    <div class="row" v-if="client">
                        <div class="column">
                            Nombre: {{ client.first_name }} {{ client.last_name || '' }}
                        </div>
                        <div class="column">
                            RUT: {{ clientRut }}
                        </div>
                        <div class="column">
                        <span v-if="client.birth_date">
                            Fecha de nacimiento: {{ $moment(client.birth_date).format('DD-MM-YYYY') }}
                        </span>
                            <span v-else>
                            Fecha de nacimiento: n/d
                        </span>
                        </div>
                        <div class="column">
                            Dirección: {{ client.address_line1 || 'n/d' }}
                        </div>
                        <div class="column">
                        <span v-if="client.birth_date">
                            Edad: {{ $moment().diff($moment(client.birth_date), 'years') }}
                        </span>
                            <span v-else>
                            Edad: n/d
                        </span>
                        </div>
                        <div class="column">
                            E-mail: {{ client.email }}
                        </div>
                        <div class="column">
                            Sexo: {{ client.gender === 'm' ? 'Hombre' : 'Mujer' }}
                        </div>
                        <div class="column">
                        <span v-if="client.phone1">
                            Teléfono: +56 {{ client.phone1 }}
                        </span>
                            <span v-else>
                            Teléfono: n/d
                        </span>
                        </div>
                        <div class="column">
                        <span v-if="client.occupation">
                            Ocupación: {{ client.occupation.description }}
                        </span>
                            <span v-else>
                            Ocupación: n/d
                        </span>
                        </div>
                        <div class="column">
                        <span v-if="client.insurance_company">
                            Sistema de salud: {{ client.insurance_company.name }}
                        </span>
                            <span v-else>
                            Sistema de salud: n/d
                        </span>
                        </div>
                    </div>

                    <div class="subtitle">
                        Datos generales del médico:
                    </div>
                    <div class="row">
                        <div class="column" v-if="specialist">
                            Nombre: {{ specialist.first_name }} {{ specialist.last_name || '' }}
                        </div>
                        <div class="column" v-if="institution">
                            Institución: {{ institution.name }}
                        </div>
                    </div>

                    <div class="title">
                        REGISTRO DE LA ATENCIÓN MÉDICA
                    </div>
                    <div class="section">
                        Fecha: {{ date }}

                        <div class="float-right">
                            Folio: {{ event && event.id }}
                        </div>
                    </div>
                </div>

                <!-- REASON -->
                <v-slide-y-transition>
                    <template v-show="reasonDescription">
                        <div v-if="reasonDescription"
                             :class="{'printable-section': true, print: printableItems.reasonDescription}">
                            <v-card-title class="pb-1" v-if="!hideTitles">
                                <div class="title">
                                    <v-checkbox
                                        class="ml-2 print-check"
                                        label="Motivo de la Cita"
                                        v-model="printableItems.reasonDescription"/>
                                </div>
                            </v-card-title>
                            <v-card-text class="py-1">
                                <div class="section py-1 pl-2 grey lighten-3">
                                    <div class="pa-1">
                                        {{ reasonDescription }}
                                    </div>
                                </div>
                            </v-card-text>
                        </div>
                    </template>
                </v-slide-y-transition>

                <!-- ANAMNESIS -->
                <v-slide-y-transition>
                    <template v-show="anamnesis">
                        <div v-if="anamnesis"
                             :class="{'printable-section': true, print: printableItems.anamnesis}">
                            <v-card-title class="pb-1" v-if="!hideTitles">
                                <div class="title">
                                    <v-checkbox
                                        class="ml-2 print-check"
                                        label="Anamnesis"
                                        v-model="printableItems.anamnesis"/>
                                </div>
                            </v-card-title>
                            <v-card-text class="py-1">
                                <div class="section py-1 pl-2 grey lighten-3">
                                    <div class="pa-1 list-item" v-html="encodeLineBreaks(anamnesis)"/>
                                </div>
                            </v-card-text>
                        </div>
                    </template>
                </v-slide-y-transition>

                <!-- PHYSICAL TREATMENT -->
                <v-slide-y-transition>
                    <template v-show="physicalTreatment">
                        <div v-if="physicalTreatment"
                             :class="{'printable-section': true, print: printableItems.physicalTreatment}">
                            <v-card-title class="pb-1" v-if="!hideTitles">
                                <div class="title">
                                    <v-checkbox
                                        class="ml-2 print-check"
                                        label="Exámen Físico"
                                        v-model="printableItems.physicalTreatment"/>
                                </div>
                            </v-card-title>
                            <v-card-text class="py-1">
                                <div class="section py-1 pl-2 grey lighten-3">
                                    <div class="pa-1 list-item" v-html="encodeLineBreaks(physicalTreatment)"/>
                                </div>
                            </v-card-text>
                        </div>
                    </template>
                </v-slide-y-transition>

                <!-- CLINICAL EVOLUTION -->
                <v-slide-y-transition>
                    <template v-show="clinicalEvolution">
                        <div v-if="clinicalEvolution"
                             :class="{'printable-section': true, print: printableItems.clinicalEvolution}">
                            <v-card-title class="pb-1" v-if="!hideTitles">
                                <div class="title">
                                    <v-checkbox
                                        class="ml-2 print-check"
                                        label="Evolución clínica"
                                        v-model="printableItems.clinicalEvolution"/>
                                </div>
                            </v-card-title>
                            <v-card-text class="py-1">
                                <div class="section py-1 pl-2 grey lighten-3">
                                    <div class="pa-1 list-item" v-html="encodeLineBreaks(clinicalEvolution)"/>
                                </div>
                            </v-card-text>
                        </div>
                    </template>
                </v-slide-y-transition>

                <!-- LABORATORY EXAMS -->
                <v-slide-y-transition>
                    <template v-show="laboratoryExams">
                        <div v-if="laboratoryExams"
                             :class="{'printable-section': true, print: printableItems.laboratoryExams}">
                            <v-card-title class="pb-1" v-if="!hideTitles">
                                <div class="title">
                                    <v-checkbox
                                        class="ml-2 print-check"
                                        label="Exámenes de laboratorio"
                                        v-model="printableItems.laboratoryExams"/>
                                </div>
                            </v-card-title>
                            <v-card-text class="py-1">
                                <div class="section py-1 pl-2 grey lighten-3">
                                    <div class="pa-1 list-item" v-html="encodeLineBreaks(laboratoryExams)"/>
                                </div>
                            </v-card-text>
                        </div>
                    </template>
                </v-slide-y-transition>

                <!-- MEDICAL DIAGNOSTICS -->
                <v-slide-y-transition>
                    <template v-show="medicalDiagnostic">
                        <div v-if="medicalDiagnostic"
                             :class="{'printable-section': true, print: printableItems.medicalDiagnostic}">
                            <v-card-title class="pb-1" v-if="!hideTitles">
                                <div class="title">
                                    <v-checkbox
                                        class="ml-2 print-check"
                                        label="Diagnóstico médico"
                                        v-model="printableItems.medicalDiagnostic"/>
                                </div>
                            </v-card-title>
                            <v-card-text class="py-1">
                                <div class="section py-1 pl-2 grey lighten-3">
                                    <div class="pa-1 list-item" v-html="encodeLineBreaks(medicalDiagnostic)"/>
                                </div>
                            </v-card-text>
                        </div>
                    </template>
                </v-slide-y-transition>

                <!-- NEXT CONTROL IN MONTHS -->
                <v-slide-y-transition>
                    <template v-show="nextControlInMonths">
                        <div v-if="nextControlInMonths"
                             :class="{'printable-section': true, print: printableItems.nextControlInMonths}">
                            <v-card-title class="pb-1" v-if="!hideTitles">
                                <div class="title">
                                    <v-checkbox
                                        class="ml-2 print-check"
                                        label="Próximo control"
                                        v-model="printableItems.nextControlInMonths"/>
                                </div>
                            </v-card-title>
                            <v-card-text class="py-1">
                                <div class="section py-1 pl-2 grey lighten-3">
                                    <div class="pa-1 list-item">
                                        {{ nextControlInMonths }}
                                    </div>
                                </div>
                            </v-card-text>
                        </div>
                    </template>
                </v-slide-y-transition>

                <!-- ADVANCED OPHTHALMOLOGICAL-->
                <template v-if="hasAdvancedTemplate">

                    <!-- MEDICAL HISTORY -->
                    <v-slide-y-transition>
                        <template v-show="medicalHistory">
                            <div v-if="medicalHistory"
                                 :class="{'printable-section': true, print: printableItems.medicalHistory}">
                                <v-card-title class="pb-1" v-if="!hideTitles">
                                    <div class="title">
                                        <v-checkbox
                                            class="ml-2 print-check"
                                            label="Historia Clínica"
                                            v-model="printableItems.medicalHistory"/>
                                    </div>
                                </v-card-title>
                                <v-card-text class="py-1">
                                    <div class="section py-1 pl-2 grey lighten-3">
                                        <div class="pa-1 list-item" v-html="encodeLineBreaks(medicalHistoryText)"/>
                                    </div>
                                </v-card-text>
                            </div>
                        </template>
                    </v-slide-y-transition>

                    <!-- PHYSICAL EXAMS -->
                    <v-slide-y-transition>
                        <template v-show="physicalExams">
                            <div v-if="physicalExams"
                                 :class="{'printable-section': true, print: printableItems.physicalExams}">
                                <v-card-title class="pb-1" v-if="!hideTitles">
                                    <div class="title">
                                        <v-checkbox
                                            class="ml-2 print-check"
                                            label="Exámenes Oftalmológicos"
                                            v-model="printableItems.physicalExams"/>
                                    </div>
                                </v-card-title>
                                <v-card-text class="py-1">
                                    <div class="section py-1 pl-2 grey lighten-3">
                                        <div class="pa-1 list-item" v-html="encodeLineBreaks(physicalExamsText)"/>
                                    </div>
                                </v-card-text>
                            </div>
                        </template>
                    </v-slide-y-transition>

                    <!-- INDICATIONS INFO -->
                    <v-slide-y-transition>
                        <template v-show="showIndication">
                            <div v-if="showIndication"
                                 :class="{'printable-section': true, print: printableItems.indication}">
                                <v-card-title class="pb-1" v-if="!hideTitles">
                                    <div class="title">
                                        <v-checkbox
                                            class="ml-2 print-check"
                                            label="Indicaciones"
                                            v-model="printableItems.indication"/>
                                    </div>
                                </v-card-title>
                                <template v-if="glasses">
                                    <v-card-text class="py-1">
                                        <div class="section py-1 pl-2 grey lighten-3">
                                            <div class="subtitle">
                                                <b>Lentes:</b>
                                            </div>
                                            <div class="pa-1 list-item" v-html="encodeLineBreaks(glasses)"/>
                                        </div>
                                    </v-card-text>
                                </template>
                                <template v-if="medicines">
                                    <v-card-text class="py-1">
                                        <div class="section py-1 pl-2 grey lighten-3">
                                            <div class="subtitle">
                                                <b>Medicamentos:</b>
                                            </div>
                                            <div class="pa-1 list-item" v-html="encodeLineBreaks(medicines)"/>
                                        </div>
                                    </v-card-text>
                                </template>
                                <template v-if="complementaryExams">
                                    <v-card-text class="py-1">
                                        <div class="section py-1 pl-2 grey lighten-3">
                                            <div class="subtitle">
                                                <b>Exámenes complementarios:</b>
                                            </div>
                                            <div class="pa-1 list-item" v-html="encodeLineBreaks(complementaryExams)"/>
                                        </div>
                                    </v-card-text>
                                </template>
                            </div>
                        </template>
                    </v-slide-y-transition>
                </template>

                <div class="printable-section print d-none">
                    <div class="subtitle signed-medical">
                        <img width="100" :src="specialist.signature_image" v-if="specialist.signature_image">
                        <br>
                        ____________________________
                        <br>
                        FIRMA DEL PROFESIONAL
                    </div>
                </div>
            </div>
        </v-card>
        <v-card v-if="electronicMedicalPrescription || electronicMedicalOrder || pathologiesGES" :flat="flat" class="kim-card mt-3 pb-2">
            <!-- ELECTRONIC MEDICAL PRESCRIPTION TITLE -->
            <template v-if="electronicMedicalPrescription && !hideTitles">
                <v-card-text class="py-2">
                    <v-layout align-center>
                        <v-flex xs12 class="print-btn text-xs-right pb-1">
                            <v-chip @click="sendPdfSummary('electronicMedicalPrescription')" label small outline color="secondary">
                                <v-progress-circular v-if="electronicMedicalPrescriptionSending" indeterminate size="20" width="2" style="padding: 0 28px;"/>
                                <div v-else>
                                    Enviar <v-icon size="14" class="ml-1">fas fa-paper-plane</v-icon>
                                </div>
                            </v-chip>
                            <v-chip @click="printSummary('electronicMedicalPrescription')" label small outline color="secondary">
                                Imprimir <v-icon size="14" class="ml-1">fas fa-print</v-icon>
                            </v-chip>
                        </v-flex>
                    </v-layout>
                    <v-layout>
                        <v-flex xs12 class="summary-title text-uppercase">
                            Receta Médica Electrónica
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </template>
            <!-- ELECTRONIC MEDICAL PRESCRIPTION PRINT -->
            <div ref="electronicMedicalPrescription" id="electronicMedicalPrescription">

                <!-- PRINCIPAL INFO -->
                <div class="printable-section print d-none">
                    <div class="title">
                        RECETA MÉDICA ELECTRÓNICA
                    </div>
                    <div class="subtitle" v-if="client">
                        Datos generales del paciente:
                    </div>
                    <div v-if="client">
                        Nombre: {{ client.first_name }} {{ client.last_name || '' }}
                    </div>
                    <div v-if="client">
                        RUT: {{ clientRut }}
                    </div>

                    <div class="divider"/>

                    <div class="subtitle">
                        Datos generales del médico:
                    </div>
                    <div v-if="specialist">
                        Nombre: {{ specialist.first_name }} {{ specialist.last_name || '' }}
                    </div>
                    <div v-if="specialist && specialist.dni">
                        RUT: {{ specialist.dni  | rut }}
                    </div>
                    <div v-else>
                        RUT: n/d
                    </div>
                    <div v-if="institution && institution.address">
                        Dirección centro médico: {{ institution.address }}
                    </div>
                    <div v-else>
                        Dirección centro médico: n/d
                    </div>
                    <div>
                        Fecha: {{ attentionDate }}
                    </div>

                    <div class="divider"/>

                    <div>
                        Rp.
                    </div>

                    <div class="divider"/>
                </div>

                <!-- INDICATIONS INFO -->
                <v-slide-y-transition>
                    <template v-show="showIndication">
                        <div v-if="showIndication" class="print d-none">
                            <v-card-title class="pb-1">
                                <div class="title">
                                    Indicaciones
                                </div>
                            </v-card-title>
                            <template v-if="glasses">
                                <v-card-text class="py-1">
                                    <div class="subtitle">
                                        Lentes:
                                    </div>
                                    <div class="section py-1 pl-2 grey lighten-3">
                                        <div class="pa-1 list-item" v-html="encodeLineBreaks(glasses)"/>
                                    </div>
                                </v-card-text>
                            </template>
                            <template v-if="medicines">
                                <v-card-text class="py-1">
                                    <div class="subtitle">
                                        Medicamentos:
                                    </div>
                                    <div class="section py-1 pl-2 grey lighten-3">
                                        <div class="pa-1 list-item" v-html="encodeLineBreaks(medicines)"/>
                                    </div>
                                </v-card-text>
                            </template>
                            <template v-if="complementaryExams">
                                <v-card-text class="py-1">
                                    <div class="subtitle">
                                        Exámenes complementarios:
                                    </div>
                                    <div class="section py-1 pl-2 grey lighten-3">
                                        <div class="pa-1 list-item" v-html="encodeLineBreaks(complementaryExams)"/>
                                    </div>
                                </v-card-text>
                            </template>
                        </div>
                    </template>
                </v-slide-y-transition>

                <!-- ELECTRONIC MEDICAL PRESCRIPTION INFO -->
                <v-slide-y-transition>
                    <template v-show="electronicMedicalPrescription">
                        <div v-if="electronicMedicalPrescription" class="printable-section print">
                            <v-card-text class="py-1">
                                <div class="py-1 pl-2 grey lighten-3">
                                    <div class="pa-1 info-section" v-html="encodeLineBreaks(electronicMedicalPrescription)"/>
                                </div>
                            </v-card-text>
                        </div>
                    </template>
                </v-slide-y-transition>

                <div class="printable-section print d-none">
                    <div class="subtitle signed-medical-footer">
                        <img width="100" :src="specialist.signature_image" v-if="specialist.signature_image">
                        <br>
                        ____________________________
                        <br>
                        FIRMA DEL PROFESIONAL
                    </div>
                </div>
            </div>

            <!-- ELECTRONIC MEDICAL ORDER TITLE -->
            <template v-if="electronicMedicalOrder && !hideTitles">
                <v-divider class="mt-2"/>
                <v-card-text class="py-2">
                    <v-layout align-center>
                        <v-flex xs12 class="print-btn text-xs-right pb-1">
                            <v-chip @click="sendPdfSummary('electronicMedicalOrder')" label small outline color="secondary">
                                <v-progress-circular v-if="electronicMedicalOrderSending" indeterminate size="20" width="2" style="padding: 0 28px;"/>
                                <div v-else>
                                    Enviar <v-icon size="14" class="ml-1">fas fa-paper-plane</v-icon>
                                </div>
                            </v-chip>
                            <v-chip @click="printSummary('electronicMedicalOrder')" label small outline color="secondary">
                                Imprimir <v-icon size="14" class="ml-1">fas fa-print</v-icon>
                            </v-chip>
                        </v-flex>
                    </v-layout>
                    <v-layout>
                        <v-flex xs12 class="summary-title text-uppercase">
                            Orden Médica Electrónica
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </template>
            <!-- ELECTRONIC MEDICAL ORDER PRINT -->
            <div id="electronicMedicalOrder">

                <!-- PRINCIPAL INFO -->
                <div class="printable-section print d-none">
                    <div class="title">
                        ORDEN MÉDICA ELECTRÓNICA
                    </div>
                    <div class="subtitle" v-if="client">
                        Datos generales del paciente:
                    </div>
                    <div v-if="client">
                        Nombre: {{ client.first_name }} {{ client.last_name || '' }}
                    </div>
                    <div v-if="client">
                        RUT: {{ clientRut }}
                    </div>
                    <div>
                        Diagnostico: {{ medicalDiagnostic }}
                    </div>

                    <div class="divider"/>

                    <div class="subtitle">
                        Datos generales del médico:
                    </div>
                    <div v-if="specialist">
                        Nombre: {{ specialist.first_name }} {{ specialist.last_name || '' }}
                    </div>
                    <div v-if="specialist && specialist.dni">
                        RUT: {{ specialist.dni  | rut }}
                    </div>
                    <div v-else>
                        RUT: n/d
                    </div>
                    <div v-if="speciality">
                        Especialidad: {{ speciality.description }}
                    </div>
                    <div v-if="institution && institution.address">
                        Dirección centro médico: {{ institution.address }}
                    </div>
                    <div v-else>
                        Dirección centro médico: n/d
                    </div>
                    <div>
                        Fecha: {{ attentionDate }}
                    </div>

                    <div class="divider"/>

                    <div>
                        Rp.
                    </div>

                    <div class="divider"/>
                </div>

                <!-- ELECTRONIC MEDICAL ORDER INFO -->
                <v-slide-y-transition>
                    <template v-show="electronicMedicalOrder">
                        <div v-if="electronicMedicalOrder" class="printable-section print">
                            <v-card-text class="py-1">
                                <div class="py-1 pl-2 grey lighten-3">
                                    <div class="pa-1 info-section" v-html="encodeLineBreaks(electronicMedicalOrder)"/>
                                </div>
                            </v-card-text>
                        </div>
                    </template>
                </v-slide-y-transition>

                <div class="printable-section print d-none">
                    <div class="subtitle signed-medical-footer">
                        <img width="100" :src="specialist.signature_image" v-if="specialist.signature_image">
                        <br>
                        ____________________________
                        <br>
                        FIRMA DEL PROFESIONAL
                    </div>
                </div>
            </div>

            <!-- PATHOLOGIES GES TITLE -->
            <template v-if="pathologiesGES && !hideTitles">
                <v-divider class="mt-2"/>
                <v-card-text class="py-2">
                    <v-layout align-center>
                        <v-flex xs12 class="print-btn text-xs-right pb-1">
                            <v-chip @click="sendPdfSummary('pathologiesGES')" label small outline color="secondary">
                                <v-progress-circular v-if="pathologiesGESSending" indeterminate size="20" width="2" style="padding: 0 28px;"/>
                                <div v-else>
                                    Enviar <v-icon size="14" class="ml-1">fas fa-paper-plane</v-icon>
                                </div>
                            </v-chip>
                            <v-chip @click="printSummary('pathologiesGES')" label small outline color="secondary">
                                Imprimir <v-icon size="14" class="ml-1">fas fa-print</v-icon>
                            </v-chip>
                        </v-flex>
                    </v-layout>
                    <v-layout>
                        <v-flex xs12 class="summary-title text-uppercase">
                            Patologías GES
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </template>
            <!-- PATHOLOGIES GES PRINT -->
            <div id="pathologiesGES">

                <!-- PRINCIPAL INFO -->
                <div class="printable-section print text-body d-none">
                    <div class="text-center">
                        <div class="title-1">
                            FORMULARIO DE CONSTANCIA DE INFORMACIÓN AL PACIENTE GES
                        </div>
                        <div class="subtitle-1">
                            (Artículo 24°, Ley 19.966)
                        </div>
                    </div>

                    <div class="bolder mt-3">
                        DATOS DEL PRESTADOR
                    </div>
                    <div class="container-box" v-if="institution && specialist">
                        <div class="mb-1">
                            INSTITUCIÓN (Hospital, Clínica, Consultorio, etc.): <span class="text-value"
                                                                                      v-html="institutionInfo.name"/>
                        </div>
                        <div class="mb-1">
                        <span class="mr-5">
                            DIRECCIÓN: <span class="text-value" v-html="institutionInfo.address"/>
                        </span>
                            <span class="mr-5">
                            CIUDAD: <span class="text-value" v-html="institutionInfo.commune"/>
                        </span>
                        </div>
                        <div class="mb-1">
                            NOMBRE PERSONA QUE NOTIFICA: <span class="text-value" v-html="institutionInfo.doctor"/>
                        </div>
                        <div>
                            RUT: <span class="text-value">{{ institutionInfo.rut | rut }}</span>
                        </div>
                    </div>

                    <div class="bolder mt-3">
                        ANTECEDENTES DEL PACIENTE
                    </div>
                    <div class="container-box" v-if="client">
                        <div class="mb-1">
                            NOMBRE: <span class="text-value" v-html="clientInfo.name"/>
                        </div>
                        <div class="mb-1">
                        <span class="mr-5">
                            RUT: <span class="text-value">{{ clientInfo.rut }}</span>
                        </span>
                            <span class="mr-5">
                            <span class="text-value" v-html="clientInfo.fonasa"/> FONASA
                        </span>
                            <span>
                            <span class="text-value" v-html="clientInfo.isapre"/> ISAPRE
                        </span>
                        </div>
                        <div class="mb-1">
                        <span class="mr-5">
                            DOMICILIO: <span class="text-value" v-html="clientInfo.address"/>
                        </span>
                            <span class="mr-5">
                            COMUNA: <span class="text-value" v-html="clientInfo.commune"/>
                        </span>
                            <span class="mr-5">
                            REGIÓN: <span class="text-value" v-html="clientInfo.region"/>
                        </span>
                        </div>
                        <div class="mb-1">
                        <span class="mr-5">
                            N° TELÉFONO FIJO: <span class="text-value" v-html="clientInfo.landline"/>
                        </span>
                            <span class="mr-5">
                            N° TELÉFONO CELULAR: <span class="text-value" v-html="clientInfo.phone"/>
                        </span>
                        </div>
                        <div>
                            DIRECCIÓN CORREO ELECTRÓNICO (E-MAIL): <span class="text-value" v-html="clientInfo.email"/>
                        </div>
                    </div>

                    <div class="bolder mt-3">
                        INFORMACIÓN MÉDICA
                    </div>
                    <div class="container-box" v-if="pathologiesGES">
                        <div class="mb-1">
                            <div>
                                CONFIRMACIÓN DIAGNÓSTICA GES:
                            </div>
                            <div class="text-value" v-html="encodeLineBreaks(pathologiesGES)"/>
                        </div>
                        <div class="mt-3 mb-1">
                        <span class="mr-5">
                            <span
                                class="border-option"
                                v-html="actualCondition === 'diagnostic_confirmation' ? 'X' : '&nbsp;&nbsp;'"
                            />
                            Confirmación Diagnóstica
                        </span>
                            <span>
                            <span
                                class="border-option"
                                v-html="actualCondition === 'patient_treatment' ? 'X' : '&nbsp;&nbsp;'"
                            />
                            Paciente en Tratamiento
                        </span>
                        </div>
                    </div>

                    <div class="bolder mt-3">
                        CONSTANCIA:
                    </div>
                    <div>
                        Declaro que, con esta fecha y hora, he tomado conocimiento que tengo derecho a acceder a las
                        Garantías Explicitas en Salud, siempre que la atención sea otorgada en la red de Prestadores que me
                        corresponde según Fonasa o Isapre, a la que me encuentro adscrito.
                    </div>
                    <div class="bolder mt-3">
                        IMPORTANTE:
                    </div>
                    <div>
                        Tenga presente que si no se cumplen las garantías usted puede reclamar ante Fonosa o a la Isapre,
                        según corresponda. Si la respuesta no es satisfactoria, usted puede recurri en segunda instancia
                        a la Superintendencia de Salud.
                    </div>

                    <div class="bolder mt-3">
                        FECHA Y HORA DE NOTIFICACIÓN: <span class="text-value" v-html="dateAttention"/>
                    </div>

                    <div class="mt-3">
                        <div class="column-signal">
                            <br><br><br><br>
                            ____________________________
                            <br>
                            <div class="bolder mt-3">
                                INFORME DIAGNÓSTICO GES
                            </div>
                            <div>
                                (Firma de persona que notifica)
                            </div>
                        </div>
                        <div class="column-signal">
                            <br><br><br><br>
                            ____________________________
                            <br>
                            <div class="bolder mt-3">
                                TOME CONOCIMIENTO
                            </div>
                            <div>
                                (Firma del paciente o representante)
                            </div>
                        </div>
                    </div>

                    <br><br>

                    <div class="mt-3">
                        <div class="mb-1">
                            En caso que la persona que tomó conocimiento no sea el paciente, identificar:
                        </div>
                        <div class="mb-1">
                            Nombre: <span class="text-value mr-5" v-html="getWhiteSpace(70)"/>
                            Rut: <span class="text-value" v-html="getWhiteSpace(30)"/>
                        </div>
                        <div>
                            N° Teléfono Celular: <span class="text-value mr-5" v-html="getWhiteSpace(25)"/>
                            Dirección correo electrónico (e-mail): <span class="text-value" v-html="getWhiteSpace(50)"/>
                        </div>
                    </div>
                </div>

                <!-- PATHOLOGIES GES INFO -->
                <v-slide-y-transition>
                    <template v-show="pathologiesGES">
                        <div v-if="pathologiesGES">
                            <v-card-text class="py-1">
                                <div class="py-1 pl-2 grey lighten-3">
                                    <div class="pa-1 no-print" v-html="encodeLineBreaks(pathologiesGES)"/>
                                </div>
                            </v-card-text>
                        </div>
                    </template>
                </v-slide-y-transition>
            </div>
        </v-card>
    </div>
</template>
<script>

import {mapGetters} from "vuex";
import {ApiUtils} from "@/utils/ApiUtils";

export default {
    props: {
        flat: {
            type: Boolean,
            default: false
        },
        event: {
            validator: prop => typeof prop === 'object' || prop === null,
            required: true
        },
        hideTitles: {
            type: Boolean,
            default: false
        },
        title: {
            type: String,
            default: 'Ficha Clínica Electrónica'
        }
    },
    data() {
        return {
            printableItems: {
                reasonDescription: false,
                anamnesis: false,
                physicalTreatment: false,
                clinicalEvolution: false,
                laboratoryExams: false,
                medicalDiagnostic: false,
                nextControlInMonths: false,

                // ADVANCED
                indication: false,
                medicalHistory: false,
                physicalExams: false,
            },
            allSelected: false,
            sendDisabled: false,
            electronicMedicalRecordSending: false,
            electronicMedicalPrescriptionSending: false,
            electronicMedicalOrderSending: false,
            pathologiesGESSending: false,
        }
    },
    computed: {
        ...mapGetters(['specialistSettings']),
        date() {
            const vm = this
            if (!vm.event) return ''
            return vm.$moment(vm.event.date).format('DD-MM-YYYY')
        },
        client() {
            const vm = this
            if (!vm.event) return null
            return vm.event.client
        },
        specialist() {
            const vm = this
            if (!vm.event) return null
            return vm.event.schedule.specialist
        },
        speciality() {
            const vm = this
            if (!vm.event) return null
            return vm.event.schedule.service_item
        },
        institution() {
            const vm = this
            if (!vm.event) return null
            return vm.event.schedule.service_provider.institution
        },
        simpleAttention() {
            const vm = this
            if (!vm.event || !vm.event.simple_attention) return null
            return vm.event.simple_attention
        },
        reasonDescription() {
            const vm = this
            return vm.event && vm.event.reason_description || null
        },
        anamnesis() {
            const vm = this
            return vm.simpleAttention && vm.simpleAttention.anamnesis || null
        },
        physicalTreatment() {
            const vm = this
            return vm.simpleAttention && vm.simpleAttention.physical_treatment || null
        },
        clinicalEvolution() {
            const vm = this
            return vm.simpleAttention && vm.simpleAttention.clinical_evolution || null
        },
        laboratoryExams() {
            const vm = this
            return vm.simpleAttention && vm.simpleAttention.laboratory_exams || null
        },
        medicalDiagnostic() {
            const vm = this
            if (!vm.event || !vm.event.diagnoses) return null
            let diagnoses = vm.event.diagnoses.map(diagnose => diagnose.name)
            return diagnoses.join(' - ')
        },
        electronicMedicalPrescription() {
            const vm = this
            return vm.simpleAttention && vm.simpleAttention.electronic_medical_prescription || null
        },
        electronicMedicalOrder() {
            const vm = this
            return vm.simpleAttention && vm.simpleAttention.electronic_medical_order || null
        },
        nextControlInMonths() {
            const vm = this
            if (vm.simpleAttention && vm.simpleAttention.next_control_in_months) {
                let next = vm.simpleAttention.next_control_in_months
                return `En ${next} ${next === '1' ? 'mes' : 'meses'}`
            }
            return null
        },
        hasPrintableItems() {
            const vm = this
            for (let attr in vm.printableItems) {
                if (vm.printableItems[attr]) {
                    return true
                }
            }
            return false
        },
        printableItemsQuantity() {
            const vm = this
            let quantity = 0
            for (let attr in vm.printableItems) {
                if (vm[attr]) quantity++
            }
            return quantity
        },
        hasAdvancedTemplate() {
            const vm = this
            return vm.specialistSettings && vm.specialistSettings.attention_template === 'advanced'
        },
        medicalHistory() {
            const vm = this
            if (!vm.event || !vm.event.medical_history_items) return null
            let hasItems = vm.event.medical_history_items.some(history => history.items.length > 0)
            if (!hasItems) return null
            return vm.event.medical_history_items
        },
        medicalHistoryText() {
            const vm = this
            let medicalHistory = ''
            vm.medicalHistory.forEach(history => {
                if (history.items.length > 0) {
                    medicalHistory += `<b>${history.description}:</b>\n`
                    history.items.forEach(item => {
                        medicalHistory += `- ${item.description}\n`
                    })
                }
            })

            return medicalHistory
        },
        physicalExams() {
            const vm = this
            if (!vm.event) return null
            return vm.event.physical_exams || null
        },
        physicalExamsText() {
            const vm = this
            let physicalExams = ''
            vm.physicalExams.forEach(exam => {
                physicalExams += `<b>${exam.name}:</b>\n`
                let values = exam.pivot.values
                if (values.length === 6) {
                    physicalExams += `OD: Sph: ${values[0].value} | Cyl: ${values[1].value} | Eje: ${values[2].value}\n`
                    physicalExams += `OI: Sph: ${values[3].value} | Cyl: ${values[4].value} | Eje: ${values[5].value}\n`
                } else {
                    physicalExams += `OD: ${values[0].value}\n`
                    physicalExams += `OI: ${values[1].value}\n`
                }
            })

            return physicalExams
        },
        indication() {
            const vm = this
            if (!vm.event) return null
            return vm.event.indication || null
        },
        showIndication() {
            const vm = this
            return vm.indication && (!!vm.glasses || !!vm.medicines || !!vm.complementaryExams)
        },
        glasses() {
            const vm = this
            if (!vm.indication || !vm.indication.glasses) return null
            let glasses = vm.indication.glasses
            return `OD: Sph: ${glasses.od_sph} | Cyl: ${glasses.od_cyl} | Eje: ${glasses.od_degrees}
                OI: Sph: ${glasses.oi_sph} | Cyl: ${glasses.oi_cyl} | Eje: ${glasses.oi_degrees}
                Add. Cerca: ${glasses.add} | DP: ${glasses.dp}`
        },
        medicines() {
            const vm = this
            if (!vm.indication || !vm.indication.medicines) return null
            let medicines = vm.indication.medicines.map(med => {
                return `- ${med.name} (${med.pivot.dose}) cada ${med.pivot.interval} hora(s) durante ${med.pivot.duration} ${vm.durationUnit(med.pivot.duration_unit)}`
            })
            return medicines.join('\n')
        },
        complementaryExams() {
            const vm = this
            if (!vm.indication || !vm.indication.complementary_exams) return null
            return vm.indication.complementary_exams.map(ce => `- ${ce.description}`).join('\n')
        },
        pathologiesGES() {
            const vm = this
            if (!vm.simpleAttention || !vm.simpleAttention.pathologies || vm.simpleAttention.pathologies.length === 0) return null
            let pathologies = vm.simpleAttention.pathologies.map(pathology => pathology.name)
            return '- ' + pathologies.join('\n- ')
        },
        actualCondition() {
            const vm = this
            return vm.simpleAttention && vm.simpleAttention.actual_condition || null
        },
        insuranceCompany() {
            const vm = this
            return vm.client && vm.client.insurance_company_id || null
        },
        isFonasa() {
            const vm = this
            return vm.insuranceCompany === 2
                ? '&nbsp;X&nbsp;' : '&nbsp;&nbsp;&nbsp;'
        },
        isIsapre() {
            const vm = this
            return (vm.insuranceCompany > 2 && vm.insuranceCompany < 8) || vm.insuranceCompany === 11
                ? '&nbsp;&nbsp;X&nbsp;&nbsp;' : '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;'
        },
        institutionInfo() {
            const vm = this
            return {
                name: vm.institution.name,
                address: vm.institution.address || vm.getWhiteSpace(30),
                commune: vm.getWhiteSpace(24),
                doctor: `${vm.specialist.first_name} ${vm.specialist.last_name || ''}`,
                rut: vm.specialist.dni,
            }
        },
        clientInfo() {
            const vm = this
            return {
                name: `${vm.client.first_name} ${vm.client.last_name || ''}`,
                rut: vm.clientRut,
                fonasa: vm.isFonasa,
                isapre: vm.isIsapre,
                address: vm.client.address || vm.getWhiteSpace(30),
                commune: vm.client.commune && vm.client.commune.name || vm.getWhiteSpace(24),
                region: vm.client.commune && vm.client.commune.region && vm.client.commune.region.name || vm.getWhiteSpace(24),
                landline: vm.client.phone2 || vm.getWhiteSpace(24),
                phone: vm.client.phone1 || vm.getWhiteSpace(24),
                email: vm.client.email || vm.getWhiteSpace(50)
            }
        },
        dateAttention() {
            const vm = this
            if(!vm.event) return ''
            return vm.$moment(vm.event.attention_started_at).format('DD-MM-YY [//] HH:mm')
        },
        showCardEvent(vm = this) {
            if (vm.reasonDescription || vm.anamnesis || vm.physicalTreatment || vm.clinicalEvolution ||
                vm.laboratoryExams || vm.medicalDiagnostic || vm.nextControlInMonths ||
                vm.electronicMedicalPrescription || vm.electronicMedicalOrder || vm.pathologiesGES) return true
            if (vm.hasAdvancedTemplate && (vm.medicalHistory || vm.physicalExams || vm.showIndication)) return true
            return false
        },
        clientRut(vm = this) {
            if (!vm.client) return 'n/d'
            let rut = vm.client.dni
            return vm.client.is_foreign ? rut : vm.$options.filters.rut(rut)
        },
        attentionDate(vm = this) {
            if (!vm.event.attention_started_at) return 'n/d'
            return vm.$moment(vm.event.attention_started_at).format('DD-MM-YYYY')
        }
    },
    methods: {
        printSummary(section) {
            this.$htmlToPaper(section)
        },
        async sendPdfSummary(section) {
            const vm = this
            if (vm.sendDisabled || vm[`${section}Sending`]) return;
            vm.sendDisabled = true
            vm[`${section}Sending`] = true

            let response = await ApiUtils.makeGet(`event/${vm.event.id}/appointment/${section}`)
            if(response.ok) {
                const json = await response.json()
                vm.toast(json.message)
            } else {
                vm.toast('Ha ocurrido un error. Inténtelo mas tarde', 'error')
            }

            vm.sendDisabled = false
            vm[`${section}Sending`] = false
        },
        encodeLineBreaks(str = '', defaultStr = '-') {
            return str ? str.replace(/\n/g, '<br>') : defaultStr
        },
        onAllSelected() {
            const vm = this
            vm.allSelected = !vm.allSelected
            for (let attr in vm.printableItems) {
                if (vm[attr]) vm.printableItems[attr] = vm.allSelected
            }
        },
        durationUnit(unit) {
            if (unit === 'days') return 'día(s)'
            if (unit === 'weeks') return 'semana(s)'
            return 'mes(es)'
        },
        getWhiteSpace(length) {
            let spaces = ''
            for (let i = 0; i < length; i++) spaces += '&nbsp'
            return spaces
        }
    },
    watch: {
        printableItems: {
            deep: true,
            handler(val) {
                let quantity = 0
                for (let attr in val) if (val[attr]) quantity++
                this.allSelected = quantity === this.printableItemsQuantity
            }
        }
    }
}
</script>

<style scoped>

.print-check {
    height: 32px;
    margin: 0;
    font-weight: bold;
}

.section {
    border-radius: 5px;
    margin: 3px;
}

.item-group {
    padding: 5px;
    border-radius: 5px;
    border: 1px solid #ccc;
    margin-right: 5px;
}

.summary-title {
    font-size: 15px;
    font-weight: bold;
    color: rgba(0, 0, 0, 0.75);
}

.checkbox-all {
    margin-top: 0;
    font-weight: bold;
}

>>> .v-label {
    font-size: 15px !important;
}


</style>